import { useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  useCancelOwnerRemoval,
  useGrantAdmin,
  useMakeOwner,
  useRemoveOwner,
  useRevokeAdmin,
  useTenantAdmins,
} from '@/api/hooks';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { planLimitOf } from '@/lib/plan';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  Input,
  formatDate,
  PersonDisplay,
  PageTitle,
  ConfirmDialog,
} from '@dataroom/shared';
import type { PendingOwnerRemoval, TenantAdmin } from '@dataroom/shared';

const ROLE_BADGE_STYLES: Record<'owner' | 'rooms_admin', string> = {
  owner: 'bg-primary/10 text-primary',
  rooms_admin: 'bg-muted text-muted-foreground',
};

function roleLabel(admin: TenantAdmin): string {
  return admin.tenantRole === 'owner' ? 'Owner' : 'Rooms Admin';
}

/** Non-self-attesting error message from a mutation error (RoomsPage/
 * RoomDetailPage's pattern: an ApiError's message is already the API's plain
 * text; anything else is a generic fallback). */
function plainError(error: unknown): string | null {
  if (error instanceof ApiError) return error.message;
  return error ? 'Request failed.' : null;
}

/** The confirmation an owner change asks for (ADR-0055). */
type OwnerChange =
  | { kind: 'make'; admin: TenantAdmin }
  | { kind: 'remove'; admin: TenantAdmin; self: boolean };

/**
 * ADR-0013 "Team" page — the tenant admin roster (owners + delegated
 * rooms_admins), with an invite-by-email form and per-row revoke. All backing
 * endpoints are OWNER-ONLY server-side (a rooms_admin gets 403); the page renders
 * a plain access message on a 403 instead of hiding the nav link — see Layout.tsx.
 *
 * Owners (ADR-0055, account recovery): a firm may have several owners. An owner
 * makes a Rooms Admin an owner, or steps themselves down (at once). Removing
 * ANOTHER owner is a request that takes effect after 72 hours; until then the
 * row says so and the person (or any other owner) can cancel it — the emailed
 * link lands here with `?cancelRemoval=<id>`, which puts that request on top.
 * The last owner cannot step down (the server refuses with `last_owner`, so the
 * button is not offered). Each change asks first and is emailed to every owner
 * and the recovery contact (Settings).
 */
export function TeamPage() {
  const roster = useTenantAdmins();
  const grantAdmin = useGrantAdmin();
  const revokeAdmin = useRevokeAdmin();
  const makeOwner = useMakeOwner();
  const removeOwner = useRemoveOwner();
  const cancelRemoval = useCancelOwnerRemoval();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [removing, setRemoving] = useState<TenantAdmin | null>(null);
  const [ownerChange, setOwnerChange] = useState<OwnerChange | null>(null);

  if (roster.error instanceof ApiError && roster.error.status === 401) {
    return <Navigate to="/login" replace />;
  }

  // A plan-limit 409 (seat_limit_reached) gets the options dialog, not a raw error.
  const seatLimit = planLimitOf(grantAdmin.error);
  const ownerOnly = roster.error instanceof ApiError && roster.error.status === 403;
  const admins = roster.data?.admins ?? [];
  const currentActorId = roster.data?.currentActorId ?? null;
  const ownerCount = admins.filter((a) => a.tenantRole === 'owner').length;
  const ownerError =
    plainError(makeOwner.error) ?? plainError(removeOwner.error) ?? plainError(cancelRemoval.error);
  const pendingRemovals = roster.data?.pendingRemovals ?? [];
  const pendingFor = (actorId: string): PendingOwnerRemoval | undefined =>
    pendingRemovals.find((p) => p.targetActorId === actorId);
  // The emailed "cancel" link opens this page with the request's id.
  const linked = pendingRemovals.find((p) => p.id === searchParams.get('cancelRemoval'));
  const emailOf = (actorId: string) => admins.find((a) => a.id === actorId)?.email ?? 'an owner';

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    grantAdmin.mutate({ email: trimmed }, { onSuccess: () => setEmail('') });
  }

  function confirmOwnerChange(change: OwnerChange) {
    makeOwner.reset();
    removeOwner.reset();
    const done = { onSettled: () => setOwnerChange(null) };
    if (change.kind === 'make') makeOwner.mutate(change.admin.id, done);
    else removeOwner.mutate(change.admin.id, done);
  }

  if (ownerOnly) {
    return (
      <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle as="h1">Team</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Only an owner can manage admins.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <div className="flex items-center gap-2">
        <PageTitle>Team</PageTitle>
        <HelpTip id="admin.team.title" />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Invite Rooms Admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="flex gap-2" onSubmit={submit}>
            <Input
              type="email"
              placeholder="person@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Admin email"
            />
            <Button type="submit" disabled={grantAdmin.isPending}>
              {grantAdmin.isPending ? 'Inviting…' : 'Invite'}
            </Button>
          </form>
          {plainError(grantAdmin.error) && !seatLimit ? (
            <p className="mt-2 text-sm text-destructive">{plainError(grantAdmin.error)}</p>
          ) : null}
        </CardContent>
      </Card>
      {seatLimit ? <PlanLimitDialog info={seatLimit} onClose={() => grantAdmin.reset()} /> : null}

      {linked ? (
        <Card aria-label="Pending owner removal">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <p className="text-sm">
              {linked.targetActorId === currentActorId
                ? 'A request to remove you as an owner'
                : `A request to remove ${emailOf(linked.targetActorId)} as an owner`}{' '}
              takes effect on {formatDate(linked.effectiveAt)} unless it is cancelled.
            </p>
            <Button
              variant="destructive"
              disabled={cancelRemoval.isPending}
              onClick={() => cancelRemoval.mutate(linked.id)}
            >
              Cancel this removal
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Owners and admins</CardTitle>
        </CardHeader>
        <CardContent>
          {roster.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : roster.error ? (
            <p className="text-sm text-destructive">{plainError(roster.error) ?? 'Request failed.'}</p>
          ) : admins.length > 0 ? (
            <>
              {ownerCount === 1 ? (
                <p className="mb-3 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                  Your firm has one owner. Make a second admin an owner so you are never locked out
                  if someone leaves, and set a recovery contact in{' '}
                  <Link to="/settings" className="font-medium text-primary hover:underline">
                    Settings
                  </Link>
                  .
                </p>
              ) : null}
              <ul className="divide-y" aria-label="Owners and admins">
                {admins.map((admin) => {
                  const isOwner = admin.tenantRole === 'owner';
                  const isSelf = admin.id === currentActorId;
                  const pending = pendingFor(admin.id);
                  const revoking = revokeAdmin.isPending && revokeAdmin.variables === admin.id;
                  return (
                    <li
                      key={admin.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-3"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <PersonDisplay email={admin.email} />
                          {isSelf ? (
                            <span className="text-xs text-muted-foreground">(you)</span>
                          ) : null}
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              ROLE_BADGE_STYLES[isOwner ? 'owner' : 'rooms_admin']
                            }`}
                          >
                            {roleLabel(admin)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Added {formatDate(admin.createdAt)}
                        </p>
                        {pending ? (
                          <p className="text-xs text-destructive">
                            Removal as owner requested
                            {pending.requestedByEmail ? ` by ${pending.requestedByEmail}` : ''} —
                            takes effect {formatDate(pending.effectiveAt)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-4">
                        {isOwner && pending ? (
                          <button
                            type="button"
                            onClick={() => cancelRemoval.mutate(pending.id)}
                            disabled={cancelRemoval.isPending}
                            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                          >
                            Cancel removal
                          </button>
                        ) : isOwner ? (
                          ownerCount > 1 ? (
                            <button
                              type="button"
                              onClick={() =>
                                setOwnerChange({ kind: 'remove', admin, self: isSelf })
                              }
                              className="text-xs font-medium text-destructive hover:underline"
                            >
                              {isSelf ? 'Step down as owner' : 'Remove owner'}
                            </button>
                          ) : null
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setOwnerChange({ kind: 'make', admin })}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Make owner
                            </button>
                            <button
                              type="button"
                              onClick={() => setRemoving(admin)}
                              disabled={revoking}
                              className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                            >
                              {revoking ? 'Removing…' : 'Revoke'}
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No admins yet.</p>
          )}
          {plainError(revokeAdmin.error) ? (
            <p className="mt-2 text-sm text-destructive">{plainError(revokeAdmin.error)}</p>
          ) : null}
          {ownerError ? <p className="mt-2 text-sm text-destructive">{ownerError}</p> : null}
        </CardContent>
      </Card>
      {removing ? (
        <ConfirmDialog
          danger
          title={`Remove ${removing.email} as a Rooms Admin?`}
          confirmLabel="Remove admin"
          pending={revokeAdmin.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={() =>
            revokeAdmin.mutate(removing.id, { onSettled: () => setRemoving(null) })
          }
        >
          <p>This removes their Rooms Admin role for your firm.</p>
        </ConfirmDialog>
      ) : null}
      {ownerChange?.kind === 'make' ? (
        <ConfirmDialog
          title={`Make ${ownerChange.admin.email} an owner?`}
          confirmLabel="Make owner"
          pending={makeOwner.isPending}
          onCancel={() => setOwnerChange(null)}
          onConfirm={() => confirmOwnerChange(ownerChange)}
        >
          <p>
            Owners can see every room in the firm and take any of them over, manage billing, add
            and remove owners and admins, and change the recovery contact. Every owner and your
            recovery contact will be emailed about this change.
          </p>
        </ConfirmDialog>
      ) : null}
      {ownerChange?.kind === 'remove' ? (
        <ConfirmDialog
          danger
          title={
            ownerChange.self
              ? 'Step down as an owner?'
              : `Remove ${ownerChange.admin.email} as an owner?`
          }
          confirmLabel={ownerChange.self ? 'Step down' : 'Request removal'}
          pending={removeOwner.isPending}
          onCancel={() => setOwnerChange(null)}
          onConfirm={() => confirmOwnerChange(ownerChange)}
        >
          {ownerChange.self ? (
            <p>
              This takes effect now. You stay a Rooms Admin with the same rooms. Every owner and
              your recovery contact will be emailed about this change.
            </p>
          ) : (
            <p>
              This takes effect in 72 hours. Until then, they or any other owner can cancel it.
              They stay a Rooms Admin with the same rooms. Every owner and your recovery contact
              will be emailed about this request.
            </p>
          )}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
